/**
 * Created by mzhao on 06/06/2022.
 * Description: Filter contacts by contact metadata.
 * ContactListFilter Component
 * ------ maintenance history ------
 * Updated by Daniel Wang, Added relationship filter
 */
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, Input, Output, EventEmitter } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { businessConstants } from '../../../constants/business.constants';
import { AppState } from '../../../redux';
import { UsersActionTypes } from '../../../redux/actions/users.actions';
import { userSettingSelector } from '../../../redux/reducers/users.reducer';
import { StoreQuerierService } from '../../../services/store-querier.service';
import { FilterMetadataType } from '../../../tamalelibs/components/filter-config';
import { ContactFilterModel, ContactFilterStatusModel, MapFilterContent, MapFilterModel } from '../../../tamalelibs/models/map-tile.model';
import { RELOAD_CONTACTS } from '../../../tamalelibs/redux/contact-list.actions';
import { ArrayHelperService } from '../../../tamalelibs/services/array-helper.service';
import { MapFilterService } from '../../../tamalelibs/services/map-filter.service';
import { FieldConfig, FieldConfigData, FieldEventType, StandardFilterItemsContact, DefaultFilterItemsContact, StandardFilterItemsContactForContactList } from '../../../widgets/field-selector/field-selector.model';
import { FilterPanelActions, FilterPanelConfig, FilterPanelEvents } from '../../../widgets/filter-item/filter-panel/filter-panel.view-model';
import { DEFAULT_PAGE_SIZE } from '../../../widgets/grid/grid.model';
@Component({
    selector: 'tam-contact-list-filter',
    templateUrl: './contact-list-filter.component.html',
    styleUrls: ['./contact-list-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContactListFilterComponent implements OnInit, OnDestroy {

    @Output() clickApplyFilter: EventEmitter<any> = new EventEmitter();

    //#region public properties
    filterConfig: FilterPanelConfig;
    fieldConfig: FieldConfig;
    isProgressing = false;
    //#endregion

    //#region private properties

    /**
    * the subscription array that would be unsubscribed on destroying.
    */
    private _contactFilterModel: ContactFilterModel;
    private _destroySubscriptions: Array<Subscription> = [];
    private _mapfilterModel = new MapFilterModel();
    private _userSetting: any;
    //#endregion

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _mapFilterService: MapFilterService,
        private _store: Store<AppState>,
        private _storeQuerier: StoreQuerierService,
    ) { }

    //#region public methods

    ngOnInit() {
        this._userSetting = this._storeQuerier.queryBySelector(userSettingSelector);
        this._initFieldSelector();
        this._fieldConfigSubject();
        this._initFilter();
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        this._destroySubscriptions = [];
    }

    clickApply() {
        // filter data ,according server design ,when user choose inclues,exclues,equals,but values equals empty ,shouldn't add this filter control.
        let data = this._contactFilterModel.metadataFilter.filters[0].filters.filter(item =>
            (item.operator !== businessConstants.mapFilter.blank && item.operator !== businessConstants.mapFilter.notBlank &&
                ((item.dataType !== FilterMetadataType.CountryState && item.values.length > 0) || (item.dataType === FilterMetadataType.CountryState && item.values && item.values.country && Object.keys(item.values.country).length > 0))) ||
            (item.operator === businessConstants.mapFilter.blank || item.operator === businessConstants.mapFilter.notBlank)
        );
        // filter relationship filter
        data = data.filter(item => (item.dataType !== FilterMetadataType.Relationship) || (item.dataType === FilterMetadataType.Relationship && item.values.length > 0 && item.target && item.target.length > 0));
        let filterParams = null;
        if (data.length > 0 || (this._contactFilterModel.exchange && this._contactFilterModel.exchange.status && this._contactFilterModel.exchange.status.length > 0)) {
            filterParams = this._contactFilterModel;
        }
        this._contactFilterModel.metadataFilter.filters[0].filters = data;
        const userSettingConfig = {
            contactFilter: {
                filterData: this.filterConfig.filtersConfigList,
                filterParams: filterParams
            }
        };
        this._store.dispatch({
            type: UsersActionTypes.UPDATE_USER_SETTING,
            payload: userSettingConfig
        });

        const action = {
            type: RELOAD_CONTACTS,
            payload: {
                pageSize: DEFAULT_PAGE_SIZE,
                page: 1,
                sortBy: 'full-name',
                sortOrder: 'asc',
                filter: this._contactFilterModel
            },
        };
        action['hasFilter'] = filterParams ? true : false;
        this.clickApplyFilter.emit(action);
    }

    clickClose() {
        this.clickApplyFilter.emit(null);
    }

    clickReset() {
        this._setDefaultFilter();
    }

    //#endregion

    //#region private methods
    private _fieldConfigSubject() {
        this._destroySubscriptions.push(
            this.fieldConfig.events$.pipe(
                filter(data => data.type === FieldEventType.select)
            ).subscribe((data) => {
                let filtersConfig;
                // contact standard values.
                if (data.payload.type === businessConstants.common.standard) {
                    filtersConfig = this._mapFilterService.getNewFilterDefaultInstance(data.payload.text, data.payload.value);
                }
                this.filterConfig.filtersConfigList.push(filtersConfig);
                this.filterConfig.action$.next({ type: FilterPanelActions.addFilter, payload: [filtersConfig] });
                this._formatFilterData();
            }),
            this.filterConfig.events$.pipe(
                filter((data) => data.type === FilterPanelEvents.change || data.type === FilterPanelEvents.delete)
            ).subscribe((data) => {
                this.filterConfig.filtersConfigList = [].concat(data.payload);
                this._formatFilterData();
            }),
        );
    }

    /**
     * contact filter format for restapi formdata.
     *
     * @private
     * @memberof TamDashboardMapNearbyComponent
     */
    private _formatFilterData() {
        this._mapfilterModel.filters[0] = new MapFilterContent();
        this._mapfilterModel.filters[0].field = businessConstants.common.contact;
        if (this.filterConfig.filtersConfigList.length > 0) {
            this._mapfilterModel.filters[0] = this._mapFilterService.getFilters(this.filterConfig.filtersConfigList, this._mapfilterModel.filters[0]);
        }
        this._contactFilterModel.exchange = new ContactFilterStatusModel();
        this.filterConfig.filtersConfigList.forEach(item => {
            if (item.metadataType === businessConstants.common.status) {
                this._contactFilterModel.exchange.status = item.value;
            }
        });
        this._contactFilterModel.metadataFilter = this._mapfilterModel;
    }

    /**
    * Initialize the Contact and Entity filter separately
    * entity use entityAdhoc for set value.
    * @param {*}
    * @memberof TamDashboardMapNearbyComponent
    */
    private _initFieldSelector() {
        // Contact
        this.filterConfig = new FilterPanelConfig();
        this.fieldConfig = new FieldConfig();
        this.fieldConfig.config = new FieldConfigData();
        this.fieldConfig.config.standardItem = StandardFilterItemsContact.concat(StandardFilterItemsContactForContactList);
        this.fieldConfig.fieldIndex = 1;
    }

    /**
     * init filter data by store or first loading.
     *
     * @private
     * @memberof ContactListFilterComponent
     */
    private _initFilter() {
        this._userSetting = this._storeQuerier.queryBySelector(userSettingSelector);
        this._contactFilterModel = new ContactFilterModel();
        if (this._userSetting && this._userSetting.contactFilter) {
            this.filterConfig.filtersConfigList = this._userSetting.contactFilter.configuration.filterData;
            this._formatFilterData();
        } else {
            // first loading filter component
            this._setDefaultFilter();
        }
    }

    /**
     *
     * set default filter data if no filter or reset.
     * @private
     * @memberof ContactListFilterComponent
     */
    private _setDefaultFilter() {
        this.filterConfig.filtersConfigList = [];
        this.filterConfig.action$.next({ type: FilterPanelActions.resetFilter });
        this._changeDetectorRef.detectChanges();
        // default filter is 'status', 'company', 'jobtitle'
        const initData: Array<any> = DefaultFilterItemsContact;
        initData.forEach(item => {
            this.fieldConfig.events$.next({ type: FieldEventType.select, payload: item });
        });
        this._formatFilterData();
    }
    //#endregion
}
